// A GameView driven by a replay's frames instead of a live snapshot stream — what lets matchView.ts, hud.ts
// and fx.ts draw a recorded match with the very code that drew it live. Each frame is turned into the
// snapshot the chosen vision would have received (format.ts's frameAsSnapshot: everything, or exactly one
// team's fog), heroes and monsters glide between two frames with the live client's own interpolation rules
// (net/entityInterp.ts: never across a fog or death boundary), and a frame's events fire once, when playback
// crosses it. A jump (a seek, a restart, a switch of vision) re-reads the world instead and fires nothing:
// the only thing replayed then is each hero's standing pose (a corpse stays down, a living hero stands).
//
// `t` is a presentation clock, not match time: it only moves forward, by the match seconds played, so every
// "until t + x" deadline (stuns, respawns) and every fx animation keeps working through seeks and pauses.
import * as THREE from 'three';
import { RULES, type HeroState } from '../../core/game.js';
import { HEROES } from '../../core/heroes.js';
import { LAYOUT, groundSpeed, type Team, type VisionSource } from '../../core/layout.js';
import type { ArtifactSlotKey, Command, RelicLine, SkillKey } from '../../core/commands.js';
import type { GameEvent, HeroId } from '../../core/events.js';
import type { MatchStats } from '../../core/stats.js';
import { MONSTERS, type MonsterState } from '../../core/monsters.js';
import type {
  GameView, HeroStatus, HeroTelegraph, ViewBarrier, ViewTotem, ViewHero, ViewLob, ViewMonster, ViewPoint, ViewPool,
  ViewProjectile, ViewReveal, ViewSummon, ViewTeamBuffs, ViewTrap, YouStats,
} from '../gameView.js';
import { applyHeroSnapshot, memoById, pruneById, remoteStatus, summonView, FULL_SIGHT, NO_REMOTE_STATUS, type RemoteHero, type RemoteMonster } from '../net/remoteGame.js';
import { PASSIVES } from '../../core/passives.js';
import { createEntityInterpolator, type EntityInterpolator } from '../net/entityInterp.js';
import { saneExtent, saneLifetime, sanedCount, wireRelics, wireSeconds } from '../net/wireSanity.js';
import { frameAsSnapshot, frameIndexAt, type ReplayFile } from '../../shared/replay/format.js';
import type {
  BarrierSnapshot, TotemSnapshot, LobSnapshot, PoolSnapshot, ProjectileSnapshot, RevealSnapshot, SeatIndex, SnapshotMessage,
  SummonSnapshot, TrapSnapshot,
} from '../../shared/protocol.js';

/** null = everything, no fog; a team = exactly what that team saw. */
export type Vision = Team | null;


type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isIndex = (v: unknown, n: number) => Number.isInteger(v) && (v as number) >= 0 && (v as number) < n;
const isVec = (v: unknown) => isObj(v) && isNum(v.x) && isNum(v.z);
const isStrings = (v: unknown) => Array.isArray(v) && v.every(s => typeof s === 'string');

/** The file's frame shapes are checked by parseReplayFile, event bodies are not: a shared file may have been
    edited by hand, and one bad event must be skipped, never break a whole render stage every frame. */
export function saneEvent(e: unknown, heroCount: number, monsterCount: number): e is GameEvent {
  if (!isObj(e)) return false;
  switch (e.type) {
    case 'anim':
      return isIndex(e.hero, heroCount) && isStrings(e.clips) && isNum(e.fade) && (e.unless === undefined || isStrings(e.unless));
    case 'num':
      return isIndex(e.hero, heroCount) && isNum(e.value);
    case 'monsterNum':
      return isIndex(e.monster, monsterCount) && isNum(e.value);
    case 'summonNum':
      return Number.isInteger(e.summon) && (e.summon as number) >= 0 && isNum(e.value);
    case 'relic':
      return (e.team === 'a' || e.team === 'b') && wireRelics({ a: e.line }).a !== null && (e.action === 'bought' || e.action === 'lost');
    case 'fx':
      return typeof e.name === 'string'
        && (e.hero === undefined || isIndex(e.hero, heroCount))
        && ['pos', 'from', 'to', 'dir'].every(k => e[k] === undefined || isVec(e[k]));
    default:
      return false;
  }
}

const numOr = (v: unknown, d: number) => (isNum(v) ? v : d);
const timed = <K extends string>(v: unknown, key: K) =>
  (Array.isArray(v) ? v : []).filter((x): x is Record<K | 'left', number> => isObj(x) && isNum(x[key]) && isNum(x.left));

/** A seat's private half, read defensively for the same reason as saneEvent. */
function youOf(seat: SnapshotMessage['you'] | undefined): YouStats {
  const s: Obj = isObj(seat) ? seat : {}, fx: Obj = isObj(s.effects) ? s.effects : {};
  const locks = (Array.isArray(s.artifactLocks) ? s.artifactLocks : [])
    .filter((l): l is YouStats['artifactLocks'][number] => isObj(l) && typeof l.line === 'string' && isNum(l.remaining));
  return {
    ink: numOr(s.ink, 0),
    inBase: false,  // a spectator never shops
    artifactLocks: locks,
    effects: {
      slows: timed(fx.slows, 'pct'), haste: timed(fx.haste, 'val'), cloakFor: numOr(fx.cloakFor, 0),
      ...(isNum(fx.stillFor) ? { stillFor: fx.stillFor } : {}),
      ...(isNum(fx.quietFor) ? { quietFor: fx.quietFor } : {}),
    },
    relicCd: wireSeconds(s.relicCd), eyeFor: wireSeconds(s.eyeFor),
  };
}

export class ReplayGameView implements GameView {
  readonly heroes: RemoteHero[];
  readonly points: ViewPoint[];
  /** The hero the HUD is built around: the viewer's own seat when known, otherwise just an anchor. */
  readonly player: RemoteHero;
  events: GameEvent[] = [];
  t = 0;
  clock = 0;
  readonly countdown = 0;
  score = { a: 0, b: 0 };
  overtime = false;
  winner: Team | null = null;
  winnerT = 0;
  matchStats: MatchStats | null = null;
  teamBuffs: ViewTeamBuffs = { a: { behemoth: null, griffin: null }, b: { behemoth: null, griffin: null } };
  relics: Record<Team, RelicLine | null> = { a: null, b: null };
  /** Running kills/deaths/assists per hero id, as of the frame on screen. */
  kda: readonly (readonly [number, number, number])[] = [];

  private visionNow: Vision = null;
  private stale = true;
  private frameIdx = -1;
  private interp: EntityInterpolator = createEntityInterpolator();
  private readonly allMonsters: RemoteMonster[];
  private wireYou: SnapshotMessage['you'] | undefined;
  private rawPools: PoolSnapshot[] = [];
  private rawTraps: TrapSnapshot[] = [];
  private rawBarriers: BarrierSnapshot[] = [];
  private rawReveals: RevealSnapshot[] = [];
  private rawTotems: TotemSnapshot[] = [];
  private rawProjectiles: ProjectileSnapshot[] = [];
  private rawLobs: LobSnapshot[] = [];
  /** This frame's summons, and the next frame's by id with its tick — a summon glides between the two. */
  private rawSummons: SummonSnapshot[] = [];
  private nextSummons = new Map<number, SummonSnapshot>();
  private summonSpan: { from: number; to: number } | null = null;
  private readonly summonViews = new Map<number, ViewSummon>();
  // Same object per wire id across frames — see RemoteGameView's own entity-getter comment (fx.ts caches
  // meshes by identity).
  private readonly poolViews = new Map<number, ViewPool>();
  private readonly trapViews = new Map<number, ViewTrap>();
  private readonly barrierViews = new Map<number, ViewBarrier>();
  private readonly revealViews = new Map<number, ViewReveal>();
  private readonly totemViews = new Map<number, ViewTotem>();
  private readonly projectileViews = new Map<number, ViewProjectile>();
  private readonly lobViews = new Map<number, ViewLob>();

  /** `viewerSeat`: the seat this browser's player held, when known (bolded like "you" live); `anchorSeat`:
      the hero the HUD hangs off — the viewer's own, else anyone on the side the results screen reads from. */
  constructor(private readonly file: ReplayFile, anchorSeat: number, viewerSeat: number | null = null) {
    this.heroes = file.header.roster.map((r, id): RemoteHero => {
      const def = HEROES[r.kind];
      const [bx, bz] = LAYOUT.bases[r.team];
      return {
        id, kind: r.kind, def, team: r.team, player: id === viewerSeat,
        pos: new THREE.Vector3(bx, 0, bz), yaw: 0, lift: 0, hp: def.hp, maxHp: def.hp, alive: true, respawnAt: 0,
        state: 'normal', stunUntil: 0, cd: { attack: 0, Q: 0, W: 0 }, swingAt: -9,
        visible: true, moving: false,
        ...NO_REMOTE_STATUS, artifacts: { E: null, R: null },
      };
    });
    this.player = this.heroes[anchorSeat] ?? this.heroes[0]!;
    this.points = LAYOUT.points.map(p => ({ id: p.id, pos: { x: p.pos[0], z: p.pos[1] }, owner: null, progress: 0, contested: false }));
    this.allMonsters = LAYOUT.bosses.map((b, id): RemoteMonster => ({
      id, kind: b.kind, pos: new THREE.Vector3(b.pos[0], 0, b.pos[1]), yaw: 0,
      hp: MONSTERS[b.kind].hp, maxHp: MONSTERS[b.kind].hp, alive: true, state: 'idle', stateT: 0, moving: false, visible: false, brandFor: 0,
    }));
  }

  get vision(): Vision { return this.visionNow; }
  setVision(v: Vision) {
    if (v === this.visionNow) return;
    this.visionNow = v;
    this.stale = true;
  }

  /** Once per render frame: the playhead (fractional tick), the match seconds played since the last call,
      and whether playback jumped rather than ran there. */
  update(tick: number, playedSec: number, jumped: boolean) {
    this.t += playedSec;
    if (this.winner !== null) this.winnerT += playedSec;
    const frames = this.file.frames;
    const idx = Math.max(0, frameIndexAt(frames, tick));
    const rebuild = jumped || this.stale || this.frameIdx < 0 || idx < this.frameIdx;
    if (rebuild) {
      this.events = [];
    } else {
      for (let i = this.frameIdx + 1; i <= idx; i++) this.pushEvents(this.snapshotAt(i).events);
    }
    if (rebuild || idx !== this.frameIdx) this.applyFrame(idx, tick);
    this.frameIdx = idx;
    this.stale = false;
    this.interpolate(tick);
    if (rebuild) this.standingPoses();
    this.settleEnd(tick);
  }

  private snapshotAt(i: number): SnapshotMessage {
    return frameAsSnapshot(this.file.frames[i]!, this.visionNow, this.player.id as SeatIndex);
  }

  private pushEvents(list: readonly unknown[]) {
    const heroCount = this.heroes.length, monsterCount = this.allMonsters.length;
    for (const e of list) if (saneEvent(e, heroCount, monsterCount)) this.events.push(e);
  }

  private applyFrame(idx: number, tick: number) {
    const a = this.snapshotAt(idx);
    const b = idx + 1 < this.file.frames.length ? this.snapshotAt(idx + 1) : null;
    this.interp = createEntityInterpolator();
    this.interp.ingest(a, 0);
    if (b) this.interp.ingest(b, 0);
    this.clock = a.clock;
    this.score = { ...a.score };
    this.overtime = a.overtime;
    this.wireYou = a.you;
    this.kda = this.file.frames[idx]!.kda;
    this.rawPools = a.pools;
    this.rawTraps = a.traps;
    this.rawBarriers = a.barriers;
    this.rawReveals = a.reveals;
    this.rawTotems = a.totems;
    this.rawProjectiles = a.projectiles;
    this.rawSummons = a.summons ?? [];
    this.nextSummons = new Map((b?.summons ?? []).map(s => [s.id, s]));
    this.summonSpan = b ? { from: a.tick, to: b.tick } : null;
    pruneById(this.summonViews, this.rawSummons);
    // A lob's `t0` is on the recorded match's clock; fx.ts measures its flight against this view's `t`.
    const matchNow = tick * this.file.header.dt;
    this.rawLobs = a.lobs.map(l => ({ ...l, t0: this.t - (matchNow - l.t0) }));
    pruneById(this.poolViews, a.pools);
    pruneById(this.trapViews, a.traps);
    pruneById(this.barrierViews, a.barriers);
    pruneById(this.revealViews, a.reveals);
    pruneById(this.totemViews, a.totems);
    pruneById(this.projectileViews, a.projectiles);
    pruneById(this.lobViews, a.lobs);
    for (const hs of a.heroes) {
      const h = this.heroes[hs.id];
      if (!h) continue;
      applyHeroSnapshot(h, hs, this.t);
      if (!isObj(h.artifacts)) h.artifacts = { E: null, R: null };
    }
    for (const ms of a.monsters ?? []) {
      const m = this.allMonsters[ms.id];
      if (!m || !ms.visible) continue;
      m.hp = ms.hp; m.maxHp = ms.maxHp; m.brandFor = numOr(ms.brandFor, 0);
    }
    if (a.teamBuffs) this.teamBuffs = a.teamBuffs;
    this.relics = wireRelics(a.relics);
    for (const ps of a.points) {
      const p = this.points.find(x => x.id === ps.id);
      if (p) { p.owner = ps.owner; p.progress = ps.progress; }
    }
  }

  private interpolate(tick: number) {
    for (const h of this.heroes) {
      const pose = this.interp.sample(h.id, tick);
      if (!pose) continue;
      if (!pose.visible) { h.visible = false; continue; }
      h.visible = true;
      h.pos.set(pose.pos.x, 0, pose.pos.z);
      h.yaw = pose.yaw;
      h.lift = pose.lift;
      h.state = pose.state as HeroState;
      h.moving = pose.moving;
    }
    for (const m of this.allMonsters) {
      const pose = this.interp.sampleMonster(m.id, tick);
      if (!pose) continue;
      if (!pose.visible) { m.visible = false; continue; }
      m.visible = true;
      m.alive = pose.alive;
      m.pos.set(pose.pos.x, 0, pose.pos.z);
      m.yaw = pose.yaw;
      m.state = pose.state as MonsterState;
      m.stateT = pose.stateT;
      m.moving = pose.moving;
    }
    const span = this.summonSpan, k = span && span.to > span.from ? Math.max(0, Math.min(1, (tick - span.from) / (span.to - span.from))) : 0;
    for (const s of this.rawSummons) {
      const v = summonView(this.summonViews, s), n = this.nextSummons.get(s.id);
      if (v && n && Number.isFinite(n.pos?.x) && Number.isFinite(n.pos?.z)) v.pos.set(s.pos.x + (n.pos.x - s.pos.x) * k, 0, s.pos.z + (n.pos.z - s.pos.z) * k);
    }
    // Derived from what this vision sees, like RemoteGameView: a fogged contest must not show.
    const R = LAYOUT.pointRadius;
    for (const p of this.points) {
      const near = this.heroes.filter(h => h.alive && h.visible && Math.hypot(h.pos.x - p.pos.x, h.pos.z - p.pos.z) < R);
      p.contested = near.some(h => h.team === 'a') && near.some(h => h.team === 'b');
    }
  }

  /** After a jump nothing replays the clip that put a model in its current pose. */
  private standingPoses() {
    for (const h of this.heroes) {
      this.events.push(h.alive
        ? { type: 'anim', hero: h.id, clips: ['Idle'], fade: 0, resetBase: true }
        : { type: 'anim', hero: h.id, clips: ['Death'], fade: 0 });
    }
  }

  /** The results screen needs the header's final word: the real winner and score (a truncated recording
      stops before them) and the stats. Without stats (an aborted match) there is no results screen. */
  private settleEnd(tick: number) {
    const { header, frames } = this.file;
    const last = frames.length - 1;
    const atEnd = this.frameIdx === last && tick >= frames[last]!.world.tick;
    const winner = atEnd && header.stats && header.winner ? header.winner : null;
    if (winner !== null && this.winner === null) this.winnerT = 0;
    this.winner = winner;
    this.matchStats = winner ? header.stats : null;
    if (atEnd) this.score = { ...header.score };
  }

  get monsters(): readonly ViewMonster[] { return this.allMonsters.filter(m => m.visible && m.alive); }
  get summons(): readonly ViewSummon[] {
    return sanedCount(this.rawSummons, 'summons').map(s => this.summonViews.get(s.id) ?? summonView(this.summonViews, s)).filter((v): v is ViewSummon => !!v);
  }
  get pools(): readonly ViewPool[] { return sanedCount(this.rawPools, 'pools').filter(z => saneExtent(z.r, 'pool.r') && saneLifetime(z.left, 'pool.left') && saneLifetime(z.life, 'pool.life')).map(z => memoById(this.poolViews, z)); }
  get traps(): readonly ViewTrap[] { return sanedCount(this.rawTraps, 'traps').filter(t => saneExtent(t.r, 'trap.r') && saneLifetime(t.armIn, 'trap.armIn') && saneLifetime(t.left, 'trap.left')).map(t => memoById(this.trapViews, t)); }
  get barriers(): readonly ViewBarrier[] { return sanedCount(this.rawBarriers, 'barriers').filter(b => saneExtent(b.half, 'barrier.half') && saneLifetime(b.left, 'barrier.left')).map(b => memoById(this.barrierViews, b)); }
  get reveals(): readonly ViewReveal[] { return sanedCount(this.rawReveals, 'reveals').filter(r => saneExtent(r.r, 'reveal.r') && saneLifetime(r.left, 'reveal.left')).map(r => memoById(this.revealViews, r)); }
  get totems(): readonly ViewTotem[] { return sanedCount(this.rawTotems, 'totems').filter(t => saneExtent(t.r, 'totem.r') && saneLifetime(t.left, 'totem.left')).map(b => memoById(this.totemViews, b)); }
  get projectiles(): readonly ViewProjectile[] { return sanedCount(this.rawProjectiles, 'projectiles').map(p => memoById(this.projectileViews, p)); }
  get lobs(): readonly ViewLob[] { return sanedCount(this.rawLobs, 'lobs').filter(l => saneExtent(l.r, 'lob.r')).map(l => memoById(this.lobViews, l)); }
  get you(): YouStats { return youOf(this.wireYou); }

  apply(_heroId: HeroId, _cmd: Command) { /* a spectator gives no orders */ }
  clearEvents() { this.events = []; }
  visibleTo(_team: Team, h: ViewHero) { return (h as RemoteHero).visible; }
  canCast(_h: ViewHero, _key: SkillKey) { return false; }
  canCastArtifact(_h: ViewHero, _slot: ArtifactSlotKey) { return false; }
  artifactCdFor(_h: ViewHero, _slot: ArtifactSlotKey) { return 0; }
  canCastRelic(_h: ViewHero) { return false; }
  chargeLevel(h: ViewHero) { return (h as RemoteHero).charge?.k ?? 0; }
  maxHpOf(h: ViewHero) { return (h as RemoteHero).maxHp; }
  movingOf(h: ViewHero) { return (h as RemoteHero).moving; }
  telegraphOf(h: ViewHero): HeroTelegraph { const r = h as RemoteHero; return { dash: r.dash, hop: r.hop }; }
  statusOf(h: ViewHero): HeroStatus { return remoteStatus(h as RemoteHero, this.t); }
  attackRangeOf(h: ViewHero) { return h.def.range; }
  groundSpeedAt(x: number, z: number) { return groundSpeed(x, z); }
  tetherSource(_h: ViewHero): ViewHero | null { return null; }
  /** The team argument is ignored on purpose: the replay's own vision decides what is in sight. */
  inSight(_team: Team, pos: { x: number; z: number }) {
    return this.visionSources().some(s => Math.hypot(s.x - pos.x, s.z - pos.z) < s.r);
  }
  /** Core's own formula (base + living heroes + reveals) for the team whose vision is shown, or the
      whole map when none is. */
  visionSources(_team?: Team): VisionSource[] {
    const team = this.visionNow;
    // The shown team's `you` is its own (frameAsSnapshot): an open Око lifts its fog.
    if (team === null || wireSeconds(this.wireYou?.eyeFor) > 0) return [FULL_SIGHT()];
    const [bx, bz] = LAYOUT.bases[team];
    return [
      { x: bx, z: bz, r: RULES.baseSight },
      ...this.heroes.filter(h => h.team === team && h.alive).map(h => ({ x: h.pos.x, z: h.pos.z, r: RULES.sight })),
      ...this.summons.filter(s => s.team === team && s.kind === 'magpie').map(s => ({ x: s.pos.x, z: s.pos.z, r: PASSIVES.birdcatcher.sight, through: true })),
      ...this.reveals.map(r => ({ x: r.x, z: r.z, r: r.r, through: true })),
    ];
  }
}
